/**
 * MemberConfirmationWidget — three-way confirm/reject/delete prompt for a
 * file paused with needs_member_confirmation (renter-interview cross-member
 * mismatch, verify mode).
 *
 * See docs/plans/sorting-hat-cross-member-and-unification.md §1. A new
 * component, deliberately NOT a reuse of the compliance-side
 * MemberAttributionSelector (WSCertificationSummary/ComplianceBulkUpload) —
 * different audience (renter, not compliance), different pause status, and
 * a different choice set (confirm/reject/delete vs. picking a member from a
 * list).
 *
 * Copy on this widget is functional placeholder text — the design doc
 * explicitly defers renter-facing UX copy to product/design (§1 Open
 * items). Do not treat any string here as final.
 *
 * Async gap: a successful POST only means the resume was *queued* — the
 * Celery task resolves the pipeline asynchronously. For confirm/reject we
 * show a persistent "submitted" panel (no false "done" state) and rely on
 * the pipeline's ordinary per-file WS events (already wired in
 * AIFileUploadV2WebSocketProvider) to move the file's status away from
 * needs_member_confirmation once it resumes — FileDetail then swaps this
 * widget out automatically. For delete we mirror FileDetail's existing
 * "Remove file" convention (deleteAIFile then navigate away immediately)
 * since there is nothing left to review once the file is gone.
 *
 * Confirm no longer moves the file immediately: the decision is *recorded*
 * and the file's data is applied to the confirmed member's survey later,
 * when the uploader runs Apply All (ENG-3054/ENG-3086). The submitted copy
 * reflects that — nothing has been written to the confirmed member's survey
 * yet.
 */
import React, { JSX, useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { resolveMemberConfirmation } from 'src/services/aiFileUploadV2';
import type { MemberConfirmationDecision } from 'src/services/aiFileUploadV2';
import type { MemberConfirmationData } from 'src/types/aiFileUploadV2';
import DocumentPreviewBox from './DocumentPreviewBox';

// ─── sx ──────────────────────────────────────────────────────────────────────

const sx = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    bgcolor: 'background.default',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    px: 2,
    py: 1.5,
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    px: 2.5,
    pt: 3,
    pb: 4,
  },
  question: {
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  body: {
    fontSize: 14,
    color: 'text.secondary',
    lineHeight: 1.5,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.25,
    mt: 1,
  },
  actionBtn: {
    height: 52,
    borderRadius: 3,
    fontSize: 14,
    fontWeight: 600,
    textTransform: 'none' as const,
  },
  submittedBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1.5,
    py: 6,
    textAlign: 'center' as const,
  },
  submittedText: {
    fontSize: 14,
    color: 'text.secondary',
  },
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MemberConfirmationWidgetProps {
  fileId: number;
  filename: string;
  filePreviewUrl: string | null;
  processedFileUrl?: string | null;
  confirmation: MemberConfirmationData;
  /** Called after a successful 'delete' decision is queued (200) — the
   *  caller should navigate away immediately, mirroring FileDetail's
   *  existing "Remove file" convention. */
  onDeleted?: (fileId: number) => void;
  /** Called after a successful 'confirm'/'reject' decision is queued
   *  (200) — the caller should navigate back to the file upload page
   *  automatically (the pipeline resumes asynchronously afterwards). */
  onSubmitted?: (fileId: number) => void;
  onBack?: () => void;
}

type WidgetPhase = 'idle' | 'submitting' | 'submitted';

// ─── MemberConfirmationWidget ─────────────────────────────────────────────────

const MemberConfirmationWidget = ({
  fileId,
  filename,
  filePreviewUrl,
  processedFileUrl,
  confirmation,
  onDeleted,
  onSubmitted,
  onBack,
}: MemberConfirmationWidgetProps): JSX.Element => {
  const [phase, setPhase] = useState<WidgetPhase>('idle');
  const [pendingDecision, setPendingDecision] =
    useState<MemberConfirmationDecision | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDecision = useCallback(
    async (decision: MemberConfirmationDecision) => {
      setPendingDecision(decision);
      setPhase('submitting');
      setError(null);
      try {
        await resolveMemberConfirmation(fileId, decision);
        if (decision === 'delete') {
          onDeleted?.(fileId);
          return;
        }
        onSubmitted?.(fileId);
        setPhase('submitted');
      } catch (err: unknown) {
        const e = err as { error?: string; detail?: string; message?: string };
        setError(
          e.error ??
            e.detail ??
            e.message ??
            'Something went wrong. Please try again.'
        );
        setPhase('idle');
        setPendingDecision(null);
      }
    },
    [fileId, onDeleted, onSubmitted]
  );

  const candidateName =
    confirmation.candidate_full_name?.trim() ||
    confirmation.extracted_name?.trim() ||
    'someone else in your household';
  const currentLabel = confirmation.current_full_name?.trim()
    ? `${confirmation.current_full_name.trim()}'s file`
    : 'this file';

  // ── Submitted — confirm/reject queued, pipeline resuming async ──────────

  if (phase === 'submitted') {
    return (
      <Box sx={sx.page} data-testid="member-confirmation-submitted">
        <Box sx={sx.header}>
          {onBack && (
            <IconButton
              size="small"
              onClick={onBack}
              aria-label="Back to file uploads"
              data-testid="member-confirmation-back-btn"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Box sx={sx.submittedBox}>
          <CircularProgress size={28} />
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
            {pendingDecision === 'confirm'
              ? `Saved — we'll add this to ${candidateName}'s file when you run Apply All.`
              : "Got it — we're updating your file now."}
          </Typography>
          <Typography sx={sx.submittedText}>
            {pendingDecision === 'confirm'
              ? 'Your file stays here for now.'
              : "This can take a few seconds. We'll let you know when it's done."}
          </Typography>
        </Box>
      </Box>
    );
  }

  const submitting = phase === 'submitting';

  return (
    <Box sx={sx.page} data-testid="member-confirmation-widget">
      <Box sx={sx.header}>
        {onBack && (
          <IconButton
            size="small"
            onClick={onBack}
            aria-label="Back to file uploads"
            data-testid="member-confirmation-back-btn"
            disabled={submitting}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <DocumentPreviewBox
        filename={filename}
        imageUrl={filePreviewUrl}
        processedFileUrl={processedFileUrl ?? null}
      />

      <Box sx={sx.content}>
        <Typography sx={sx.question}>
          Does this file belong to someone else?
        </Typography>
        <Typography sx={sx.body} data-testid="member-confirmation-body">
          We think this document may belong to <b>{candidateName}</b>, but
          it&apos;s currently attached to <b>{currentLabel}</b>.
        </Typography>

        {error && (
          <Alert severity="error" data-testid="member-confirmation-error">
            {error}
          </Alert>
        )}

        <Box sx={sx.actions}>
          <Button
            variant="contained"
            fullWidth
            sx={sx.actionBtn}
            disabled={submitting}
            onClick={() => handleDecision('confirm')}
            data-testid="member-confirmation-confirm-btn"
          >
            {submitting && pendingDecision === 'confirm' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress
                  size={16}
                  thickness={3}
                  sx={{ color: 'inherit' }}
                />
                <span>Moving file&hellip;</span>
              </Box>
            ) : (
              <span>Yes, move it to {candidateName}</span>
            )}
          </Button>

          <Button
            variant="outlined"
            fullWidth
            sx={sx.actionBtn}
            disabled={submitting}
            onClick={() => handleDecision('reject')}
            data-testid="member-confirmation-reject-btn"
          >
            {submitting && pendingDecision === 'reject' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} thickness={3} />
                <span>Saving&hellip;</span>
              </Box>
            ) : (
              <span>No, this is mine &mdash; keep it here</span>
            )}
          </Button>

          <Button
            variant="text"
            color="error"
            fullWidth
            sx={sx.actionBtn}
            disabled={submitting}
            onClick={() => handleDecision('delete')}
            data-testid="member-confirmation-delete-btn"
          >
            {submitting && pendingDecision === 'delete' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress
                  size={16}
                  thickness={3}
                  sx={{ color: 'inherit' }}
                />
                <span>Removing&hellip;</span>
              </Box>
            ) : (
              <span>Remove this file</span>
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MemberConfirmationWidget;
